import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { buildAppMeta } from "@/lib/seo";
import { REGISTRATION_STATUS_LABELS, fmtClinicPrice, publicClinicUrl } from "@/lib/clinics";
import { fmtBookingDate, fmtSlotTime } from "@/lib/booking";
import {
  getManagedClinicRegistration,
  cancelManagedClinicRegistration,
  createClinicCheckout,
} from "@/lib/clinics.functions";
import { CalendarCheck2, Clock, GraduationCap, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/clinic/r/$token")({
  head: () => ({
    meta: buildAppMeta({
      title: "Manage your clinic registration",
      description: "View or cancel your clinic spot.",
      path: "/clinic/r",
    }),
  }),
  component: ManageClinicRegistrationPage,
});

function ManageClinicRegistrationPage() {
  const { token } = Route.useParams();
  const getReg = useServerFn(getManagedClinicRegistration);
  const cancelReg = useServerFn(cancelManagedClinicRegistration);
  const startCheckout = useServerFn(createClinicCheckout);
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const justPaid =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("paid") === "1";

  const regQ = useQuery({
    queryKey: ["managed-clinic-registration", token],
    queryFn: async () => await getReg({ data: { token } }),
    retry: 1,
    // After Checkout returns, poll briefly so the webhook's status flip shows up.
    refetchInterval: (q) => (justPaid && q.state.data?.status === "pending_payment" ? 2000 : false),
  });

  const reg = regQ.data;

  async function onPay() {
    setPaying(true);
    try {
      const res = await startCheckout({
        data: { manageToken: token, origin: window.location.origin },
      });
      if (res.ok) {
        window.location.href = res.url;
      } else {
        toast.error(
          res.error === "hold_expired" || res.error === "not_payable"
            ? "This spot's payment window has passed."
            : res.error === "subscription_required" || res.error === "payments_unavailable"
              ? // Neutral on purpose — never expose the coach's billing status.
                "Online payment isn't available for this clinic — please contact your coach to arrange payment."
              : "Couldn't open checkout — please try again.",
        );
        setPaying(false);
        regQ.refetch();
      }
    } catch {
      toast.error("Couldn't open checkout — please try again.");
      setPaying(false);
    }
  }

  async function onCancel() {
    setBusy(true);
    try {
      const res = await cancelReg({ data: { token } });
      if (res.ok) {
        toast.success("Your spot has been cancelled.");
        regQ.refetch();
      } else {
        toast.error(
          res.error === "already_started"
            ? "This clinic has already started."
            : res.error === "too_many_requests"
              ? "Too many attempts — try again in a few minutes."
              : "Couldn't cancel — please try again.",
        );
      }
    } catch {
      toast.error("Couldn't cancel — please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (regQ.isLoading) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </Shell>
    );
  }

  if (!reg) {
    return (
      <Shell>
        <Card className="p-8 text-center">
          <GraduationCap className="mx-auto h-10 w-10 text-muted-foreground/50" />
          <p className="mt-3 text-sm text-muted-foreground">
            We couldn't find that registration. Check the link from your confirmation email.
          </p>
        </Card>
      </Shell>
    );
  }

  const c = reg.clinic;
  const live =
    reg.status === "registered" || reg.status === "pending_payment" || reg.status === "waitlisted";
  const clinicCancelled = c.status === "cancelled";

  return (
    <Shell>
      <Card className="p-6">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {c.coach_name}
        </p>
        <div className="mt-1 flex items-center justify-between gap-2">
          <h1 className="text-xl font-bold">{c.title}</h1>
          <Badge variant={live ? "secondary" : "outline"}>
            {clinicCancelled ? "Clinic cancelled" : REGISTRATION_STATUS_LABELS[reg.status]}
          </Badge>
        </div>

        <div className="mt-4 space-y-1.5 text-sm">
          <p className="flex items-center gap-2">
            <CalendarCheck2 className="h-4 w-4 text-muted-foreground" />
            {fmtBookingDate(c.date)}
          </p>
          <p className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            {fmtSlotTime(c.start)} – {fmtSlotTime(c.end)}
          </p>
          {c.location_name && (
            <p className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              {c.location_name}
              {c.location_city ? ` — ${c.location_city}` : ""}
            </p>
          )}
          {c.price_cents > 0 && (
            <p className="text-muted-foreground">
              {fmtClinicPrice(c.price_cents, c.currency)}
              {reg.paid ? " · paid" : ""}
            </p>
          )}
        </div>

        {clinicCancelled && (
          <p className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
            {c.coach_name} cancelled this clinic.
            {reg.paid ? " Contact them about your payment." : ""}
          </p>
        )}

        {!clinicCancelled && reg.status === "registered" && reg.paid && justPaid && (
          <p className="mt-4 rounded-md border border-success/30 bg-success/5 p-3 text-sm">
            Payment received — your spot is confirmed. A receipt and confirmation email are on their
            way.
          </p>
        )}

        {!clinicCancelled && reg.status === "pending_payment" && (
          <div className="mt-4 rounded-md border border-primary/30 bg-primary/5 p-3">
            <p className="text-sm">
              {justPaid
                ? "Confirming your payment…"
                : `Your spot is held pending payment${
                    reg.payment_due_at
                      ? ` until ${new Date(reg.payment_due_at).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })}`
                      : ""
                  }.`}
            </p>
            {!justPaid && (
              <Button className="mt-2 w-full" onClick={onPay} disabled={paying}>
                {paying && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Complete payment — {fmtClinicPrice(c.price_cents, c.currency)}
              </Button>
            )}
          </div>
        )}

        {!clinicCancelled && reg.status === "waitlisted" && (
          <p className="mt-4 text-sm text-muted-foreground">
            You're on the waitlist — we'll email you if a spot opens up.
          </p>
        )}

        {!clinicCancelled && live && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="mt-5 w-full" disabled={busy}>
                {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                {reg.status === "waitlisted" ? "Leave the waitlist" : "Cancel my spot"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {reg.status === "waitlisted" ? "Leave the waitlist?" : "Cancel your spot?"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {reg.status === "waitlisted"
                    ? "You'll be removed from the waitlist for this clinic."
                    : "Your spot will be released to the next person on the waitlist. This can't be undone."}
                  {reg.paid ? " Contact your coach about refunds." : ""}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep my spot</AlertDialogCancel>
                <AlertDialogAction onClick={onCancel}>
                  {reg.status === "waitlisted" ? "Leave waitlist" : "Cancel spot"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {!live && !clinicCancelled && (
          <div className="mt-5 text-center">
            <p className="text-sm text-muted-foreground">
              {reg.status === "cancelled" && "You cancelled this registration."}
              {reg.status === "removed" && "The coach removed this registration."}
              {reg.status === "expired" && "Your payment window expired and the spot was released."}
            </p>
            <Button variant="outline" size="sm" className="mt-3" asChild>
              <a href={publicClinicUrl(c.public_token)}>Sign up again</a>
            </Button>
          </div>
        )}
      </Card>
      <p className="mt-4 text-center text-xs text-muted-foreground">Powered by CoachQuick</p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-muted/30 px-4 py-10">
      <div className="mx-auto w-full max-w-md">{children}</div>
    </div>
  );
}
